import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ethers } from 'ethers'
import { mintMG, redeemMG, loadVaultBalances } from '../store/interactions'

const GoldVault = ({ embedded }) => {
  const dispatch = useDispatch()
  const provider = useSelector(state => state.provider.connection)
  const account = useSelector(state => state.provider.account)
  const vault = useSelector(state => state.vault.contract)
  const mgtvToken = useSelector(state => state.vault.mgtvToken)
  const tokens = useSelector(state => state.tokens.contracts)
  const paxgLocked = useSelector(state => state.vault.paxgLocked)
  const mgtvBalance = useSelector(state => state.vault.mgtvBalance)
  const paxgBalance = useSelector(state => state.vault.paxgBalance)
  const mgBalance = useSelector(state => state.vault.mgBalance)
  const transaction = useSelector(state => state.exchange.transaction)

  const [activeTab, setActiveTab] = useState('mint')
  const [amount, setAmount] = useState('')
  const [useMgtv, setUseMgtv] = useState(false)

  const MG_PER_PAXG = 31103.5

  useEffect(() => {
    if (vault && vault.address && mgtvToken && tokens && tokens[0] && account) {
      // tokens[0] = MG, find PAXG from vault
      const loadBalances = async () => {
        try {
          const paxgAddr = await vault.paxg()
          const paxgContract = new ethers.Contract(paxgAddr, tokens[0].interface, provider)
          const mgAddr = await vault.mg()
          const mgContract = new ethers.Contract(mgAddr, tokens[0].interface, provider)
          loadVaultBalances(vault, mgtvToken, paxgContract, mgContract, account, dispatch)
        } catch (e) {
          console.error('Error loading vault balances:', e)
        }
      }
      loadBalances()
    }
  }, [vault, mgtvToken, tokens, account, provider, dispatch, transaction])

  const handleMint = async (e) => {
    e.preventDefault()
    if (!amount || parseFloat(amount) <= 0) return

    const paxgAddr = await vault.paxg()
    const paxgContract = new ethers.Contract(paxgAddr, tokens[0].interface, provider)

    await mintMG(provider, vault, paxgContract, mgtvToken, amount, useMgtv, dispatch)
    setAmount('')
  }

  const handleRedeem = async (e) => {
    e.preventDefault()
    if (!amount || parseFloat(amount) <= 0) return

    const mgAddr = await vault.mg()
    const mgContract = new ethers.Contract(mgAddr, tokens[0].interface, provider)

    await redeemMG(provider, vault, mgContract, mgtvToken, amount, useMgtv, dispatch)
    setAmount('')
  }

  const getEstimate = () => {
    if (!amount || parseFloat(amount) <= 0) return '0'
    const val = parseFloat(amount)

    if (activeTab === 'mint') {
      const mgAmount = val * MG_PER_PAXG
      if (useMgtv) return mgAmount.toFixed(4)
      const fee = mgAmount * 0.0369
      return (mgAmount - fee).toFixed(4)
    } else {
      const paxgAmount = val / MG_PER_PAXG
      if (useMgtv) return paxgAmount.toFixed(6)
      const fee = paxgAmount * 0.0369
      return (paxgAmount - fee).toFixed(6)
    }
  }

  const getFeeDisplay = () => {
    if (!amount || parseFloat(amount) <= 0) return '--'
    const val = parseFloat(amount)

    if (activeTab === 'mint') {
      const mgAmount = val * MG_PER_PAXG
      if (useMgtv) {
        const mgtvBurned = mgAmount * 0.00369
        return `${mgtvBurned.toFixed(4)} MGTV burn fee`
      }
      return `${(mgAmount * 0.0369).toFixed(4)} MG to treasury`
    } else {
      if (useMgtv) {
        const mgtvBurned = val * 0.00369
        return `${mgtvBurned.toFixed(4)} MGTV burn fee`
      }
      const paxgAmount = val / MG_PER_PAXG
      return `${(paxgAmount * 0.0369).toFixed(6)} PAXG to treasury`
    }
  }

  if (!vault || !vault.address) {
    return <div className="vault-container"><p>Loading vault...</p></div>
  }

  return (
    <div className="vault-container">
      <div className="vault-header">
        <svg className="vault-emblem" width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="vaultGold" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#DAA520" />
              <stop offset="50%" stopColor="#FFD700" />
              <stop offset="100%" stopColor="#B8960C" />
            </linearGradient>
            <linearGradient id="vaultBronze" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#8B6914" />
              <stop offset="100%" stopColor="#A67C00" />
            </linearGradient>
          </defs>
          {/* Outer vault door circle */}
          <circle cx="40" cy="40" r="36" stroke="url(#vaultGold)" strokeWidth="2.5" fill="none" />
          {/* Gear notches at 8 compass points */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
            <rect
              key={angle}
              x="37" y="1" width="6" height="8" rx="1.5"
              fill="url(#vaultBronze)"
              transform={`rotate(${angle} 40 40)`}
            />
          ))}
          {/* Inner bronze ring */}
          <circle cx="40" cy="40" r="26" stroke="url(#vaultBronze)" strokeWidth="2" fill="none" />
          {/* Decorative arcs */}
          <path d="M 22 24 A 22 22 0 0 1 58 24" stroke="url(#vaultGold)" strokeWidth="1" fill="none" opacity="0.5" />
          <path d="M 22 56 A 22 22 0 0 0 58 56" stroke="url(#vaultGold)" strokeWidth="1" fill="none" opacity="0.5" />
          {/* Keyhole */}
          <circle cx="40" cy="36" r="5" fill="url(#vaultGold)" />
          <rect x="37.5" y="36" width="5" height="10" rx="1" fill="url(#vaultGold)" />
        </svg>
        <h2 className="vault-title">Gold Vault</h2>
        <span className="vault-subtitle">Fortius Quo Fidelius</span>
      </div>

      <div className="vault-stats">
        <div className="vault-stats__frame" />
        <div className="vault-stats__row">
          <span>PAXG Locked</span>
          <strong>{paxgLocked ? Number(paxgLocked).toFixed(4) : '0'} PAXG</strong>
        </div>
        <div className="vault-stats__row">
          <span>Conversion</span>
          <strong>1 PAXG = 31,103.5 MG</strong>
        </div>
        <div className="vault-stats__divider" />
        <div className="vault-stats__row">
          <span>Your PAXG</span>
          <strong>{paxgBalance ? Number(paxgBalance).toFixed(4) : '0'}</strong>
        </div>
        <div className="vault-stats__row">
          <span>Your MG</span>
          <strong>{mgBalance ? Number(mgBalance).toFixed(4) : '0'}</strong>
        </div>
        <div className="vault-stats__row">
          <span>Your MGTV</span>
          <strong>{mgtvBalance ? Number(mgtvBalance).toFixed(4) : '0'}</strong>
        </div>
        <div className="vault-stats__frame-bottom" />
      </div>

      <div className="vault-tabs">
        <button
          className={`vault-tabs__btn ${activeTab === 'mint' ? 'vault-tabs__btn--active' : ''}`}
          onClick={() => { setActiveTab('mint'); setAmount('') }}
        >
          Mint MG
        </button>
        <button
          className={`vault-tabs__btn ${activeTab === 'redeem' ? 'vault-tabs__btn--active' : ''}`}
          onClick={() => { setActiveTab('redeem'); setAmount('') }}
        >
          Redeem PAXG
        </button>
      </div>

      <form onSubmit={activeTab === 'mint' ? handleMint : handleRedeem} className="vault-form">
        <label className="vault-form__label">
          {activeTab === 'mint' ? 'PAXG Amount' : 'MG Amount'}
        </label>
        <div className="vault-form__input-row">
          <input
            type="number"
            className="vault-form__input"
            placeholder={activeTab === 'mint' ? 'Enter PAXG amount' : 'Enter MG amount'}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step="any"
            min="0"
          />
          <button
            type="button"
            className="vault-form__max-btn"
            onClick={() => setAmount(activeTab === 'mint' ? paxgBalance : mgBalance)}
          >
            MAX
          </button>
        </div>

        <div className="vault-form__estimate">
          <span>You receive:</span>
          <strong>
            {getEstimate()} {activeTab === 'mint' ? 'MG' : 'PAXG'}
          </strong>
        </div>

        <div className="vault-form__fee">
          <span>{useMgtv ? 'MGTV Burn (0.369%)' : 'Fee (3.69%)'}:</span>
          <span>{getFeeDisplay()}</span>
        </div>

        <label className="vault-form__toggle">
          <input
            type="checkbox"
            checked={useMgtv}
            onChange={(e) => setUseMgtv(e.target.checked)}
          />
          <span>Pay with MGTV — no fee, just burn</span>
        </label>

        <button
          type="submit"
          className="vault-form__submit"
          disabled={!amount || parseFloat(amount) <= 0 || (transaction && transaction.isPending)}
        >
          {transaction && transaction.isPending
            ? 'Processing...'
            : activeTab === 'mint' ? 'Mint MG' : 'Redeem PAXG'
          }
        </button>
      </form>
    </div>
  )
}

export default GoldVault
